import { Component, DestroyRef, effect, inject, input, output } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatIconButton } from '@angular/material/button';
import { MatCheckbox } from '@angular/material/checkbox';
import { MatError, MatFormField, MatLabel } from '@angular/material/form-field';
import { MatIcon } from '@angular/material/icon';
import { MatInput } from '@angular/material/input';
import { MatOption, MatSelect } from '@angular/material/select';
import { MatTooltip } from '@angular/material/tooltip';
import { TranslocoDirective } from '@jsverse/transloco';
import { BillingParty, CustomerType } from '../domain/signup';
import { trimmedEmail } from './trimmed-email';

/**
 * The invoiced party, as one form both surfaces render: the funnel collects it, the portal corrects
 * it.
 *
 * **One component for both, deliberately.** These are the fields of a § 14 UStG invoice header and
 * they are only correct together, which is also why the portal's update is a full replacement rather
 * than a patch. Two forms would be two definitions of what a valid invoice header is, and the one
 * that drifted would be the one that produced an invoice a tax audit rejects.
 *
 * **`CONSUMER` is offered but disabled, with a note saying why.** Removing the option entirely would
 * leave a business-only form with a `customerType` field that has exactly one value — which reads as
 * an oversight rather than a decision. Showing it disabled says what the platform does not yet sell,
 * and both the gateway and Admin answer 400 for it, so nothing depends on the control staying
 * disabled.
 *
 * **Changing the VAT ID clears its stored VIES confirmation**, and the form warns before it does.
 * That rule is the server's, because it is about what makes an invoice compliant; the warning is the
 * form's, because the customer is the only one who can decide whether they meant to.
 */
@Component({
  selector: 'pp-billing-party-form',
  imports: [ReactiveFormsModule, MatFormField, MatLabel, MatError, MatInput, MatSelect, MatOption, MatCheckbox, MatIcon, MatIconButton, MatTooltip, TranslocoDirective],
  template: `
    <ng-container *transloco="let t; prefix: 'platform_subscription.billing_party'">
      <form [formGroup]="form" class="billing-party-form">
        <mat-form-field>
          <mat-label>{{ t('legalName') }}</mat-label>
          <input id="billing-legal-name" matInput formControlName="legalName" autocomplete="organization" />
          <button type="button" mat-icon-button matSuffix [matTooltip]="t('legalNameHint')" [attr.aria-label]="t('legalNameHint')">
            <mat-icon>info_outline</mat-icon>
          </button>
          @if (form.controls.legalName.hasError('required') && form.controls.legalName.touched) {
            <mat-error>{{ t('legalNameRequired') }}</mat-error>
          }
        </mat-form-field>

        <mat-form-field>
          <mat-label>{{ t('legalForm') }}</mat-label>
          <input id="billing-legal-form" matInput formControlName="legalForm" autocomplete="off" />
          <button type="button" mat-icon-button matSuffix [matTooltip]="t('legalFormHint')" [attr.aria-label]="t('legalFormHint')">
            <mat-icon>info_outline</mat-icon>
          </button>
          @if (form.controls.legalForm.hasError('required') && form.controls.legalForm.touched) {
            <mat-error>{{ t('legalFormRequired') }}</mat-error>
          }
        </mat-form-field>

        <mat-form-field>
          <mat-label>{{ t('customerType') }}</mat-label>
          <mat-select id="billing-customer-type" formControlName="customerType">
            <mat-option [value]="customerTypes.BUSINESS">{{ t('customerTypes.BUSINESS') }}</mat-option>
            <!-- Disabled rather than absent: see the class comment. v1 answers 400 for it. -->
            <mat-option [value]="customerTypes.CONSUMER" [disabled]="true">{{ t('customerTypes.CONSUMER') }}</mat-option>
          </mat-select>
          <button type="button" mat-icon-button matSuffix [matTooltip]="t('customerTypeHint')" [attr.aria-label]="t('customerTypeHint')">
            <mat-icon>info_outline</mat-icon>
          </button>
        </mat-form-field>

        <fieldset class="address-group" formGroupName="registeredAddress">
          <legend>{{ t('registeredAddress') }}</legend>
          <mat-form-field>
            <mat-label>{{ t('street') }}</mat-label>
            <input id="billing-registered-street" matInput formControlName="street" autocomplete="street-address" />
            @if (form.controls.registeredAddress.controls.street.hasError('required') && form.controls.registeredAddress.controls.street.touched) {
              <mat-error>{{ t('required') }}</mat-error>
            }
          </mat-form-field>
          <mat-form-field>
            <mat-label>{{ t('postalCode') }}</mat-label>
            <input id="billing-registered-postal-code" matInput formControlName="postalCode" autocomplete="postal-code" />
            @if (form.controls.registeredAddress.controls.postalCode.hasError('required') && form.controls.registeredAddress.controls.postalCode.touched) {
              <mat-error>{{ t('required') }}</mat-error>
            }
          </mat-form-field>
          <mat-form-field>
            <mat-label>{{ t('city') }}</mat-label>
            <input id="billing-registered-city" matInput formControlName="city" autocomplete="address-level2" />
            @if (form.controls.registeredAddress.controls.city.hasError('required') && form.controls.registeredAddress.controls.city.touched) {
              <mat-error>{{ t('required') }}</mat-error>
            }
          </mat-form-field>
          <mat-form-field>
            <mat-label>{{ t('country') }}</mat-label>
            <input id="billing-registered-country" matInput formControlName="country" maxlength="2" autocomplete="country" />
            <button type="button" mat-icon-button matSuffix [matTooltip]="t('countryHint')" [attr.aria-label]="t('countryHint')">
              <mat-icon>info_outline</mat-icon>
            </button>
            @if (form.controls.registeredAddress.controls.country.hasError('required') || form.controls.registeredAddress.controls.country.hasError('pattern')) {
              <mat-error>{{ t('countryInvalid') }}</mat-error>
            }
          </mat-form-field>
        </fieldset>

        <mat-checkbox id="billing-separate-address" formControlName="separateBillingAddress">{{ t('separateBillingAddress') }}</mat-checkbox>

        @if (form.controls.separateBillingAddress.value) {
          <fieldset class="address-group" formGroupName="billingAddress" test-id="billing-address-group">
            <legend>{{ t('billingAddress') }}</legend>
            <mat-form-field>
              <mat-label>{{ t('street') }}</mat-label>
              <input id="billing-billing-street" matInput formControlName="street" />
            </mat-form-field>
            <mat-form-field>
              <mat-label>{{ t('postalCode') }}</mat-label>
              <input id="billing-billing-postal-code" matInput formControlName="postalCode" />
            </mat-form-field>
            <mat-form-field>
              <mat-label>{{ t('city') }}</mat-label>
              <input id="billing-billing-city" matInput formControlName="city" />
            </mat-form-field>
            <mat-form-field>
              <mat-label>{{ t('country') }}</mat-label>
              <input id="billing-billing-country" matInput formControlName="country" maxlength="2" />
            </mat-form-field>
          </fieldset>
        }

        <mat-form-field>
          <mat-label>{{ t('vatId') }}</mat-label>
          <input id="billing-vat-id" matInput formControlName="vatId" autocomplete="off" spellcheck="false" />
          <button type="button" mat-icon-button matSuffix [matTooltip]="t('vatIdHint')" [attr.aria-label]="t('vatIdHint')">
            <mat-icon>info_outline</mat-icon>
          </button>
        </mat-form-field>

        <!-- Only while editing an existing party whose VAT ID is being changed. On the funnel there
             is no stored confirmation to lose, so the warning would be noise. -->
        @if (vatIdChanged()) {
          <p class="vat-warning" test-id="billing-vat-revalidation">
            <mat-icon>info</mat-icon>
            <span>{{ t('vatIdRevalidation') }}</span>
          </p>
        }

        <mat-form-field>
          <mat-label>{{ t('registrationNumber') }}</mat-label>
          <input id="billing-registration-number" matInput formControlName="registrationNumber" autocomplete="off" />
          <button type="button" mat-icon-button matSuffix [matTooltip]="t('registrationNumberHint')" [attr.aria-label]="t('registrationNumberHint')">
            <mat-icon>info_outline</mat-icon>
          </button>
        </mat-form-field>

        <mat-form-field>
          <mat-label>{{ t('billingEmail') }}</mat-label>
          <input id="billing-email" matInput type="email" formControlName="billingEmail" autocomplete="email" />
          <button type="button" mat-icon-button matSuffix [matTooltip]="t('billingEmailHint')" [attr.aria-label]="t('billingEmailHint')">
            <mat-icon>info_outline</mat-icon>
          </button>
          @if (form.controls.billingEmail.hasError('required') && form.controls.billingEmail.touched) {
            <mat-error>{{ t('billingEmailRequired') }}</mat-error>
          }
          @if (form.controls.billingEmail.hasError('email')) {
            <mat-error>{{ t('billingEmailInvalid') }}</mat-error>
          }
        </mat-form-field>
      </form>
    </ng-container>
  `,
  styles: [
    `
      .billing-party-form {
        display: flex;
        flex-direction: column;
        row-gap: 8px;
        max-width: 520px;
      }

      .address-group {
        display: flex;
        flex-direction: column;
        row-gap: 8px;
        border: 1px solid rgba(0, 0, 0, 0.12);
        border-radius: 4px;
        padding: 8px 12px;
      }

      .address-group legend {
        font-size: 0.85rem;
        opacity: 0.75;
        padding: 0 4px;
      }

      .vat-warning {
        display: flex;
        align-items: flex-start;
        gap: 8px;
        font-size: 0.85rem;
      }
    `,
  ],
})
export class BillingPartyFormComponent {
  /**
   * What to populate the form with: a stored party in the portal, a restored draft in the funnel,
   * or nothing on a first visit.
   *
   * `Partial` rather than `BillingParty`, because the funnel's draft is by definition incomplete —
   * it is what survived `sessionStorage` mid-way through the form. The portal always passes a
   * complete one, which a `Partial` accepts.
   */
  readonly billingParty = input<Partial<BillingParty> | undefined>(undefined);

  /** Emitted on every change, valid or not — the host decides what to do with an invalid form. */
  readonly changed = output<Partial<BillingParty>>();

  readonly customerTypes = CustomerType;

  private readonly formBuilder = inject(FormBuilder);
  private readonly destroyRef = inject(DestroyRef);

  readonly form = this.formBuilder.nonNullable.group({
    legalName: ['', Validators.required],
    legalForm: ['', Validators.required],
    customerType: [CustomerType.BUSINESS, Validators.required],
    registeredAddress: this.formBuilder.nonNullable.group({
      street: ['', Validators.required],
      postalCode: ['', Validators.required],
      city: ['', Validators.required],
      // Uppercased on submit by the mapper; the pattern accepts either case so that an autofilled
      // lower-case value is not reported as an error the customer cannot see the cause of.
      country: ['', [Validators.required, Validators.pattern(/^[A-Za-z]{2}$/)]],
    }),
    separateBillingAddress: [false],
    billingAddress: this.formBuilder.nonNullable.group({
      street: [''],
      postalCode: [''],
      city: [''],
      country: [''],
    }),
    vatId: [''],
    registrationNumber: [''],
    billingEmail: ['', [Validators.required, trimmedEmail]],
  });

  constructor() {
    // `effect` rather than `ngOnInit`, because the portal patches this in once the account loads —
    // which is after the component exists. An `ngOnInit` read would see `undefined` and the form
    // would stay empty behind a populated account.
    effect(() => {
      const existing = this.billingParty();
      if (!existing) return;
      const emptyAddress = { street: '', postalCode: '', city: '', country: '' };
      this.form.patchValue({
        legalName: existing.legalName ?? '',
        legalForm: existing.legalForm ?? '',
        customerType: existing.customerType ?? CustomerType.BUSINESS,
        registeredAddress: { ...emptyAddress, ...existing.registeredAddress },
        separateBillingAddress: !!existing.billingAddress,
        billingAddress: { ...emptyAddress, ...existing.billingAddress },
        vatId: existing.vatId ?? '',
        registrationNumber: existing.registrationNumber ?? '',
        billingEmail: existing.billingEmail ?? '',
      });
    });

    this.form.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => this.changed.emit(this.value()));
  }

  /**
   * Whether the customer has typed a VAT ID different from the stored one.
   *
   * Requires a party that has been saved — `legalName` present is the proxy for that. On the funnel
   * the input is a restored draft rather than a stored row, so there is no VIES confirmation to lose
   * and the warning would be describing something that has not happened.
   */
  vatIdChanged(): boolean {
    const existing = this.billingParty();
    if (!existing?.legalName) return false;
    return (this.form.controls.vatId.value ?? '').trim() !== (existing.vatId ?? '').trim();
  }

  /**
   * The form as the contract's shape.
   *
   * `separateBillingAddress` does not travel: it is a control that exists to decide whether the
   * billing address is sent at all. Absent means "invoices go to the registered address", which is a
   * different statement from a copy of it — a copy would stop following the registered address the
   * moment it changed.
   */
  value(): Partial<BillingParty> {
    const raw = this.form.getRawValue();
    return {
      legalName: raw.legalName,
      legalForm: raw.legalForm,
      customerType: raw.customerType,
      registeredAddress: raw.registeredAddress,
      billingAddress: raw.separateBillingAddress ? raw.billingAddress : undefined,
      vatId: raw.vatId,
      registrationNumber: raw.registrationNumber,
      billingEmail: raw.billingEmail,
    };
  }
}
